import { computed, signal } from '@preact/signals';
import { directFetcher, proxyFetcher, type Fetcher } from '../../core/transport/fetcher';
import { LogTransport, type RetryInfo } from '../../core/transport/logTransport';
import { ReachabilityStore } from '../../core/transport/reachability';
import { BUILTIN_PROXY, builtinProxyAvailable } from './proxy';
import { settings } from './settings';
import { storage } from './storage';

export const reachability = new ReachabilityStore(storage);

/** Bumped whenever reachability changes so components re-render badges. */
export const reachabilityVersion = signal(0);
reachability.subscribe(() => {
  reachabilityVersion.value++;
});

/** Live "waiting for the log" notices (rate limiting), keyed by log id. */
export const retryNotices = signal<Record<string, RetryInfo>>({});

/** Extra fetchers registered by a host (the desktop app adds its CORS-free fetcher here). */
export const hostFetchers = signal<Fetcher[]>([]);

/** The proxy base URL in use ('' when none): the built-in one, the user's own, or nothing. */
export const activeProxyUrl = computed<string>(() => {
  const s = settings.value;
  if (s.proxy === 'builtin') return builtinProxyAvailable() ? BUILTIN_PROXY.url : '';
  if (s.proxy === 'custom') return s.proxyUrl.trim();
  return '';
});

/** True while the user has not decided yet and the built-in proxy could be offered. */
export const proxyOffer = computed(() => settings.value.proxy === 'ask' && builtinProxyAvailable() && hostFetchers.value.length === 0);

// Direct first, always; the proxy is only tried for requests the browser blocks (see LogTransport).
const fetchers = computed<Fetcher[]>(() => {
  const host = hostFetchers.value;
  if (host.length > 0) return host;
  const out: Fetcher[] = [directFetcher];
  const proxy = activeProxyUrl.value;
  if (proxy) out.push(proxyFetcher(proxy));
  return out;
});

export const transport = new LogTransport({
  fetchers: () => fetchers.value,
  reachability,
  onRetry: (info) => {
    retryNotices.value = { ...retryNotices.value, [info.logId]: info };
    setTimeout(() => {
      const { [info.logId]: _gone, ...rest } = retryNotices.value;
      retryNotices.value = rest;
    }, info.delayMs);
  },
});

export const hasProxy = computed(() => activeProxyUrl.value !== '' || hostFetchers.value.length > 0);
